import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Code, 
  Copy, 
  Check,
  Zap,
  ArrowRight,
  Layers,
  Terminal
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get(`${API}/templates`);
        setTemplates(response.data);
      } catch (error) {
        console.error('Error fetching templates:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const industryColors = {
    'CRM': 'bg-blue-950/30 text-blue-400 border-blue-900/50',
    'Construction': 'bg-amber-950/30 text-amber-400 border-amber-900/50',
    'Finance': 'bg-emerald-950/30 text-emerald-400 border-emerald-900/50',
    'Developer Tools': 'bg-purple-950/30 text-purple-400 border-purple-900/50',
    'E-commerce': 'bg-pink-950/30 text-pink-400 border-pink-900/50',
  };

  return (
    <div className="pt-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <Badge className="mb-4 bg-purple-950/30 text-purple-400 border-purple-900/50">
            <Layers className="w-3.5 h-3.5 mr-1.5" />
            Starter Kits
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
            Templates & Workflows
          </h1>
          <p className="text-lg text-slate-400">
            Pre-built workflows to get you started in minutes. Copy, paste, and customize.
          </p>
        </div>

        {/* Templates Grid */}
        <div className="space-y-8">
          {templates.map((template, index) => (
            <div 
              key={template.id}
              className="bg-card border border-white/5 rounded-2xl overflow-hidden"
              data-testid={`template-${template.id}`}
            >
              {/* Template Header */}
              <div className="p-6 border-b border-white/5">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="font-heading text-2xl font-semibold text-white">
                        {template.name}
                      </h2>
                      <Badge className={industryColors[template.industry] || 'bg-slate-800 text-slate-300'}>
                        {template.industry}
                      </Badge>
                    </div>
                    <p className="text-slate-400">{template.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {template.connectors.map((connector, i) => (
                      <Badge 
                        key={i}
                        variant="outline" 
                        className="border-slate-700 text-slate-300"
                      >
                        {connector}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              {/* Code Snippets */}
              <div className="p-6">
                <Tabs defaultValue="python" className="w-full">
                  <TabsList className="bg-secondary mb-4">
                    <TabsTrigger value="python" className="flex items-center gap-2">
                      <Terminal className="w-4 h-4" />
                      Python
                    </TabsTrigger>
                    <TabsTrigger value="javascript" className="flex items-center gap-2">
                      <Code className="w-4 h-4" />
                      JavaScript
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="python">
                    <div className="relative">
                      <div className="terminal rounded-lg overflow-hidden">
                        <div className="terminal-header px-4 py-2 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-red-500/50" />
                            <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                            <div className="w-3 h-3 rounded-full bg-green-500/50" />
                            <span className="text-xs text-slate-500 ml-2 font-mono">workflow.py</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-500 hover:text-white"
                            onClick={() => copyToClipboard(template.code_snippets.python, `${template.id}-python`)}
                          >
                            {copied === `${template.id}-python` ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        <pre className="p-4 overflow-x-auto text-sm text-blue-300 font-mono leading-relaxed">
                          {template.code_snippets.python}
                        </pre>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="javascript">
                    <div className="relative">
                      <div className="terminal rounded-lg overflow-hidden">
                        <div className="terminal-header px-4 py-2 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-red-500/50" />
                            <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                            <div className="w-3 h-3 rounded-full bg-green-500/50" />
                            <span className="text-xs text-slate-500 ml-2 font-mono">workflow.js</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-500 hover:text-white"
                            onClick={() => copyToClipboard(template.code_snippets.javascript, `${template.id}-js`)}
                          >
                            {copied === `${template.id}-js` ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        <pre className="p-4 overflow-x-auto text-sm text-blue-300 font-mono leading-relaxed">
                          {template.code_snippets.javascript}
                        </pre>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>

                {/* Use Case */}
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-slate-500">
                    <span className="text-slate-400">Use case:</span> {template.use_case}
                  </p>
                  <Link to="/marketplace">
                    <Button variant="ghost" className="text-primary hover:text-primary/80">
                      View Connectors
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <h3 className="font-heading text-2xl font-semibold text-white mb-4">
            Need a custom workflow?
          </h3>
          <p className="text-slate-400 mb-6">
            Browse our marketplace to find the right connectors for your use case
          </p>
          <Link to="/marketplace">
            <Button className="glow-primary bg-primary hover:bg-primary/90">
              <Zap className="w-5 h-5 mr-2" />
              Explore Marketplace
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TemplatesPage;
